import { useModelStore } from '@/store';
import { exploreApi } from '@/lib/api';

// Preferred image-to-video models, ordered by quality. Resolution is now
// performed server-side (see backend/routes/jsonRoutes.js) and returned by the
// /json/explore/:slug/source-file endpoint, so this list is only used as a
// fallback when the backend doesn't return a model. Keep it in sync with the
// backend's I2V_SLUG_PREFERENCES.
export const I2V_SLUG_PREFERENCES = [
  'veo-3-1-img-2-vid',
  'wan-2-7-img-2-vid',
  'hailuo-2-3-pro-img-2-vid',
  'kling-3-0-pro-img-2-vid',
  'seedance-2-0-img-2-vid',
  'kling-2-5-turbo-pro-img-2-vid',
  'hailuo-2-3-img-2-vid',
  'veo-3-1-fast-img-2-vid',
];

// Mint a user-owned File from an explore image's CDN object so it can be
// attached to the chat input (as the source image for image-to-video). When
// the source is an image the backend also returns the best available i2v
// model, which we hydrate into the model store and return as `i2vModelId`.
//
// Returns: { file, i2vModelId } — either field may be null on failure.
export async function mintExploreImageForVideo(exploreItemSlug) {
  if (!exploreItemSlug) return { file: null, i2vModelId: null };
  try {
    const { file, imageToVideoModel } = await exploreApi.createSourceFile(exploreItemSlug);
    let i2vModelId = null;
    if (imageToVideoModel) {
      // Hydrate the model store so downstream UIs (e.g. model selector)
      // recognise it without a separate fetch.
      try { useModelStore.getState().addModels?.(imageToVideoModel); } catch {}
      i2vModelId = imageToVideoModel.id || null;
    } else if (file) {
      // Fallback: if the backend didn't surface a preferred model (e.g. older
      // backend, no i2v model seeded), fall back to the local resolver.
      i2vModelId = await resolveImageToVideoModelIdFromStore();
    }
    return { file: file || null, i2vModelId };
  } catch (err) {
    console.warn('[explore] Failed to mint i2v source file:', err);
    return { file: null, i2vModelId: null };
  }
}

// Local fallback resolver — looks up i2v models against whatever is currently
// in the model store cache; only used when the backend response is missing.
async function resolveImageToVideoModelIdFromStore() {
  const store = useModelStore.getState();
  for (const slug of I2V_SLUG_PREFERENCES) {
    const cached = store.getModelBySlug?.(slug);
    if (cached?.id) return cached.id;
  }
  for (const slug of I2V_SLUG_PREFERENCES) {
    try {
      const fetched = await store.fetchModelBySlug?.(slug);
      if (fetched?.id) return fetched.id;
    } catch {}
  }
  return null;
}

// Backwards-compatible names for legacy call sites.
export const resolveImageToVideoModelId = resolveImageToVideoModelIdFromStore;
export async function mintExploreSourceFile(exploreItemSlug) {
  const { file } = await mintExploreImageForVideo(exploreItemSlug);
  return file;
}
